import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MatchBadge } from "@/components/MatchBadge";
import { ArrowLeft, MessageSquare, CheckCircle2, Loader2, User } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { APPLICATION_STATUS_CONFIG, getStatusLabel } from "@/lib/applicationStatus";
import { calculateMatchScore } from "@/lib/skills";
import { format } from "date-fns";

export default function EmpresaVagaCandidaturaDetalhe() {
  const { id: vagaId, applicationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [newNote, setNewNote] = useState("");

  // Fetch application
  const { data: application, isLoading: appLoading } = useQuery({
    queryKey: ["application_detail", applicationId],
    enabled: !!applicationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_applications")
        .select("*, profiles(full_name, city, state, objective, bio), job_posts(title, required_skills, nice_skills)")
        .eq("id", applicationId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Fetch timeline events
  const { data: events, refetch: refetchEvents } = useQuery({
    queryKey: ["application_events_detail", applicationId],
    enabled: !!applicationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("application_status_events")
        .select("*, profiles:actor_user_id(full_name)")
        .eq("application_id", applicationId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch candidate skills
  const candidateId = application?.user_id;
  const { data: candidateSkills } = useQuery({
    queryKey: ["candidate_skills_detail", candidateId],
    enabled: !!candidateId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills" as any)
        .select("skill_id")
        .eq("user_id", candidateId!);
      if (error) throw error;
      return (data as any[]).map((s) => s.skill_id as string);
    },
  });

  const profile = application?.profiles as any;
  const job = application?.job_posts as any;
  const currentStatus = application?.status ?? "aplicada";
  const sc = APPLICATION_STATUS_CONFIG[currentStatus] ?? { label: currentStatus, color: "bg-muted text-muted-foreground", nextStates: [] };

  const requiredSkills = Array.isArray(job?.required_skills) ? (job.required_skills as string[]) : [];
  const niceSkills = Array.isArray(job?.nice_skills) ? (job.nice_skills as string[]) : [];
  const matchResult = candidateSkills ? calculateMatchScore(candidateSkills, requiredSkills, niceSkills) : null;

  const handleStatusChange = async (newStatus: string) => {
    try {
      const { error: updateError } = await supabase
        .from("job_applications")
        .update({ status: newStatus })
        .eq("id", applicationId!);
      if (updateError) throw updateError;

      await supabase.from("application_status_events").insert({
        application_id: applicationId!,
        from_status: currentStatus,
        to_status: newStatus,
        actor_user_id: user!.id,
      });

      refetchEvents();
      toast({ title: "Status atualizado", description: `Candidato movido para ${getStatusLabel(newStatus)}` });
    } catch {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await supabase.from("application_status_events").insert({
        application_id: applicationId!,
        from_status: currentStatus,
        to_status: currentStatus,
        actor_user_id: user!.id,
        note: newNote.trim(),
      });
      setNewNote("");
      refetchEvents();
      toast({ title: "Nota adicionada" });
    } catch {
      toast({ title: "Erro ao adicionar nota", variant: "destructive" });
    }
  };

  if (appLoading) {
    return (
      <div className="page-container-desktop flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="page-container-desktop text-center py-12">
        <p className="text-muted-foreground">Candidatura não encontrada.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(`/empresa/vagas/${vagaId}/candidaturas`)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="page-container-desktop max-w-4xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/empresa/vagas/${vagaId}/candidaturas`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              Pipeline — {profile?.full_name ?? "Candidato"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {job?.title} • {[profile?.city, profile?.state].filter(Boolean).join(", ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {matchResult && <MatchBadge percentage={matchResult.percentage} size="lg" />}
          <Button variant="outline" onClick={() => navigate(`/empresa/talento/${candidateId}`)}>Ver Perfil</Button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground">Status Atual</h2>
              <Badge className={sc.color}>{sc.label}</Badge>
            </div>

            {sc.nextStates.length > 0 && (
              <div className="flex gap-2 mb-6 flex-wrap">
                {sc.nextStates.map((ns) => (
                  <Button key={ns} size="sm" variant={ns === "nao_avancou" ? "outline" : "hero"} onClick={() => handleStatusChange(ns)}>
                    {getStatusLabel(ns)}
                  </Button>
                ))}
              </div>
            )}

            <h3 className="font-medium text-foreground mb-4">Histórico</h3>
            <div className="relative">
              <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-border" />
              <div className="space-y-4">
                {(events ?? []).map((event) => (
                  <div key={event.id} className="flex items-start gap-4 relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${event.note && event.from_status === event.to_status ? 'bg-secondary' : 'bg-primary text-primary-foreground'}`}>
                      {event.note && event.from_status === event.to_status
                        ? <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        : <CheckCircle2 className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-sm font-medium text-foreground">
                        {event.from_status === event.to_status
                          ? "Nota adicionada"
                          : `${getStatusLabel(event.from_status ?? "")} → ${getStatusLabel(event.to_status)}`}
                      </p>
                      {event.note && (
                        <p className="text-sm text-muted-foreground mt-1 bg-secondary/50 rounded-lg p-2">{event.note}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {(event as any).profiles?.full_name ?? "Sistema"} • {format(new Date(event.created_at), "dd/MM/yyyy HH:mm")}
                      </p>
                    </div>
                  </div>
                ))}
                {(events ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground pl-12">Nenhum evento registrado.</p>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div>
          {/* Add Note */}
          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="bg-card rounded-2xl p-5 border border-border mb-4">
            <h3 className="font-heading font-semibold text-foreground mb-3">Adicionar Nota</h3>
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Observações sobre o candidato..." rows={3} className="resize-none mb-3" />
            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()} className="w-full">
              <MessageSquare className="w-4 h-4 mr-1" /> Adicionar
            </Button>
          </motion.div>

          {/* Match details */}
          {matchResult && (
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-card rounded-2xl p-5 border border-border">
              <h3 className="font-heading font-semibold text-foreground mb-3">Habilidades</h3>
              <div className="space-y-2">
                {[...matchResult.matchedRequired, ...matchResult.matchedNice].map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-success" /> {s}
                  </div>
                ))}
                {[...matchResult.missingRequired, ...matchResult.missingNice].map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" /> {s}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
